"use client";

import React from "react";
import Image from "next/image";
import { 
  Building, 
  MapPin, 
  BedDouble, 
  Bath, 
  Maximize2, 
  Calendar, 
  FileDown, 
  Sparkles,
  CheckCircle,
  ExternalLink
} from "lucide-react";
import { PropertyItem, FEATURED_PROPERTIES } from "@/lib/demos/propertiesData";

interface PropertyGridProps {
  cityFilter: string;
  typeFilter: string;
  priceFilter: string;
  currency: "INR" | "USD";
  onSelectSiteVisit: (property: PropertyItem) => void;
  onSelectBrochure: (property: PropertyItem) => void;
}

export default function PropertyGrid({
  cityFilter,
  typeFilter,
  priceFilter,
  currency,
  onSelectSiteVisit,
  onSelectBrochure,
}: PropertyGridProps) {
  const filtered = FEATURED_PROPERTIES.filter((item) => {
    if (cityFilter !== "ALL" && item.city !== cityFilter) return false;
    if (typeFilter !== "ALL" && item.type !== typeFilter) return false;

    if (priceFilter === "under5") {
      if (currency === "INR" && item.numericINR >= 50000000) return false;
      if (currency === "USD" && item.numericUSD >= 600000) return false;
    } else if (priceFilter === "5to10") {
      if (currency === "INR" && (item.numericINR < 50000000 || item.numericINR > 100000000)) return false;
      if (currency === "USD" && (item.numericUSD < 600000 || item.numericUSD > 1500000)) return false;
    } else if (priceFilter === "above10") {
      if (currency === "INR" && item.numericINR <= 100000000) return false;
      if (currency === "USD" && item.numericUSD <= 1500000) return false;
    }

    return true;
  });

  return (
    <div className="py-14 bg-slate-900 text-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold text-amber-400 uppercase tracking-widest mb-1.5">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Available Private Allocations</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white font-heading">
              Featured Signature Portfolio
            </h2>
          </div>
          <div className="text-xs text-slate-400 font-medium">
            Showing <span className="text-amber-400 font-bold">{filtered.length}</span> of {FEATURED_PROPERTIES.length} signature properties
          </div>
        </div>

        {/* Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 px-4 rounded-2xl bg-slate-950 border border-slate-800">
            <Building className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-white mb-1">No matching properties</h3>
            <p className="text-xs text-slate-400 max-w-sm mx-auto mb-4">
              Try adjusting your corridor or budget filters to explore our complete global private allocation.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {filtered.map((prop) => {
              const priceDisplay = currency === "INR" ? prop.priceINR : prop.priceUSD;

              return (
                <div
                  key={prop.id}
                  className="group rounded-2xl bg-slate-950 border border-slate-800 hover:border-amber-500/50 shadow-xl hover:shadow-2xl hover:shadow-amber-500/10 transition-all duration-300 flex flex-col overflow-hidden"
                >
                  {/* Property Image & Badges */}
                  <div className="relative aspect-[16/10] w-full overflow-hidden bg-slate-900">
                    <img
                      src={prop.image}
                      alt={prop.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent opacity-80" />

                    {/* Top Badges */}
                    <div className="absolute top-3 left-3 flex items-center gap-2">
                      {prop.badge && (
                        <span className="px-2.5 py-1 rounded-md bg-amber-500/90 text-slate-950 font-bold text-[10px] tracking-wide uppercase shadow-md">
                          {prop.badge}
                        </span>
                      )}
                      <span className="px-2 py-1 rounded-md bg-slate-900/80 backdrop-blur-md border border-slate-700/80 text-white font-semibold text-[10px]">
                        {prop.type}
                      </span>
                    </div>

                    {/* Bottom Price Overlay */}
                    <div className="absolute bottom-3 left-3 right-3 flex items-baseline justify-between">
                      <div className="text-2xl font-extrabold text-white font-heading tracking-tight drop-shadow-md">
                        {priceDisplay}
                      </div>
                      <span className="text-[11px] font-semibold text-amber-300 bg-amber-950/70 border border-amber-500/30 px-2 py-0.5 rounded backdrop-blur-xs">
                        Verified Title
                      </span>
                    </div>
                  </div>

                  {/* Details Content */}
                  <div className="p-5 flex flex-col flex-grow">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="text-lg font-bold text-white group-hover:text-amber-400 transition-colors line-clamp-1 font-heading">
                        {prop.title}
                      </h3>
                    </div>

                    <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-3">
                      <MapPin className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                      <span>{prop.location}</span>
                    </div>

                    {/* Specs Pills */}
                    <div className="grid grid-cols-3 gap-2 py-3 px-3 rounded-xl bg-slate-900/80 border border-slate-800 text-xs font-semibold text-slate-300 mb-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <BedDouble className="w-3.5 h-3.5 text-amber-400" />
                        <span>{typeof prop.beds === "number" ? `${prop.beds} BHK` : prop.beds}</span>
                      </div>
                      <div className="flex items-center justify-center gap-1 border-x border-slate-800">
                        <Bath className="w-3.5 h-3.5 text-amber-400" />
                        <span>{prop.baths} Baths</span>
                      </div>
                      <div className="flex items-center justify-center gap-1">
                        <Maximize2 className="w-3.5 h-3.5 text-amber-400" />
                        <span>{prop.areaSqFt.toLocaleString()} sq.ft</span>
                      </div>
                    </div>

                    {/* Features list */}
                    <div className="space-y-1.5 mb-5 flex-grow">
                      {prop.features.slice(0, 3).map((feat, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs text-slate-300">
                          <CheckCircle className="w-3 h-3 text-emerald-400 shrink-0" />
                          <span className="line-clamp-1">{feat}</span>
                        </div>
                      ))}
                    </div>

                    {/* Action CTAs */}
                    <div className="grid grid-cols-2 gap-2.5 pt-3 border-t border-slate-800/80">
                      <button
                        type="button"
                        onClick={() => onSelectBrochure(prop)}
                        className="w-full py-2 px-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-200 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
                      >
                        <FileDown className="w-3.5 h-3.5 text-amber-400" />
                        <span>Get Dossier</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => onSelectSiteVisit(prop)}
                        className="w-full py-2 px-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold text-xs shadow-md flex items-center justify-center gap-1.5 transition-all hover:scale-[1.02]"
                      >
                        <Calendar className="w-3.5 h-3.5" />
                        <span>VIP Viewing</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
